<template>
  <div class="admin-registrations">
    <!-- 搜索和筛选区域 -->
    <div class="search-section">
      <el-card>
        <div class="search-form">
          <el-row :gutter="20">
            <el-col :span="6">
              <el-input v-model="searchForm.keyword" :placeholder="$t('admin.registrations.search.placeholder')"
                clearable @keyup.enter="handleSearch">
                <template #prefix>
                  <el-icon>
                    <Search />
                  </el-icon>
                </template>
              </el-input>
            </el-col>
            <el-col :span="4">
              <el-select v-model="searchForm.participationType"
                :placeholder="$t('register.form.participationType.placeholder')" clearable>
                <el-option :label="$t('register.form.participationType.offline')" value="offline" />
                <el-option :label="$t('register.form.participationType.online')" value="online" />
              </el-select>
            </el-col>
            <el-col :span="4">
              <el-select v-model="searchForm.registrationType"
                :placeholder="$t('register.form.registrationType.placeholder')" clearable>
                <el-option :label="$t('register.form.registrationType.postgraduate')" value="postgraduate" />
                <el-option :label="$t('register.form.registrationType.undergraduate')" value="undergraduate" />
                <el-option :label="$t('register.form.registrationType.faculty')" value="faculty" />
                <el-option :label="$t('register.form.registrationType.regular_attendee')" value="regular_attendee" />
              </el-select>
            </el-col>
            <el-col :span="6">
              <el-date-picker v-model="searchForm.dateRange" type="daterange" :range-separator="$t('common.to')"
                :start-placeholder="$t('common.startDate')" :end-placeholder="$t('common.endDate')" format="YYYY-MM-DD"
                value-format="YYYY-MM-DD" clearable />
            </el-col>
            <el-col :span="4">
              <el-button type="primary" @click="handleSearch">
                <el-icon>
                  <Search />
                </el-icon>
                {{ $t('admin.registrations.search.button') }}
              </el-button>
              <el-button @click="handleReset">
                <el-icon>
                  <Refresh />
                </el-icon>
                {{ $t('common.refresh') }}
              </el-button>
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card>
        <div class="action-buttons">
          <el-button type="success" @click="handleRefresh">
            <el-icon>
              <Refresh />
            </el-icon>
            {{ $t('admin.registrations.actions.refresh') }}
          </el-button>
          <el-button type="primary" @click="handleExport">
            <el-icon>
              <Download />
            </el-icon>
            {{ $t('admin.registrations.actions.export') }}
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card>
        <el-table v-loading="loading" :data="tableData" stripe border style="width: 100%"
          :empty-text="$t('admin.registrations.table.noData')">
          <el-table-column prop="id" :label="$t('admin.registrations.table.id')" width="80" align="center" />
          <el-table-column prop="first_name" :label="$t('admin.registrations.table.firstName')" width="120" />
          <el-table-column prop="last_name" :label="$t('admin.registrations.table.lastName')" width="120" />
          <el-table-column prop="email" :label="$t('admin.registrations.table.email')" width="200"
            show-overflow-tooltip />
          <el-table-column prop="phone" :label="$t('admin.registrations.table.phone')" width="150" />
          <el-table-column prop="id_number" :label="$t('admin.registrations.table.idNumber')" width="180"
            show-overflow-tooltip>
            <template #default="{ row }">
              {{ row.id_number || '-' }}
            </template>
          </el-table-column>
          <el-table-column prop="affiliation" :label="$t('admin.registrations.table.affiliation')" width="200"
            show-overflow-tooltip />
          <el-table-column prop="participation_type" :label="$t('admin.registrations.table.participationType')"
            width="120" align="center">
            <template #default="{ row }">
              <el-tag :type="row.participation_type === 'offline' ? 'success' : 'info'" size="small">
                {{ getParticipationTypeText(row.participation_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="registrationType" :label="$t('admin.registrations.table.registrationType')" width="120"
            align="center">
            <template #default="{ row }">
              <el-tag :type="getRegistrationTypeColor(row.registration_type)" size="small">
                {{ getRegistrationTypeText(row.registration_type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="job_title" :label="$t('admin.registrations.table.jobTitle')" width="150"
            show-overflow-tooltip />
          <el-table-column prop="country" :label="$t('admin.registrations.table.country')" width="120" />
          <el-table-column prop="createdAt" :label="$t('admin.registrations.table.registrationTime')" width="180"
            align="center">
            <template #default="{ row }">
              {{ formatDate(row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('admin.registrations.table.actions')" width="180" align="center" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" size="small" @click="handleView(row)">
                {{ $t('admin.registrations.table.view') }}
              </el-button>
              <el-button type="danger" size="small" @click="handleDelete(row)">
                {{ $t('admin.registrations.table.delete') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]" :total="pagination.total" layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange" @current-change="handleCurrentChange">
            <template #total>
              {{ $t('admin.registrations.pagination.total', { total: pagination.total }) }}
            </template>
          </el-pagination>
        </div>
      </el-card>
    </div>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" :title="$t('admin.registrations.modal.title')" width="600px"
      :close-on-click-modal="false">
      <div v-if="currentRecord" class="detail-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item :label="$t('admin.registrations.table.id')">
            {{ currentRecord.id }}
          </el-descriptions-item>
           <el-descriptions-item :label="$t('admin.registrations.table.phone')">
            {{ currentRecord.phone }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.firstName')">
            {{ currentRecord.first_name }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.lastName')">
            {{ currentRecord.last_name }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.email')">
            {{ currentRecord.email }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.idNumber')">
            {{ currentRecord.id_number || '-' }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.affiliation')">
            {{ currentRecord.affiliation }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.participationType')">
            <el-tag :type="currentRecord.participation_type === 'offline' ? 'success' : 'info'" size="small">
              {{ getParticipationTypeText(currentRecord.participation_type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.registrationType')">
            <el-tag :type="getRegistrationTypeColor(currentRecord.registration_type)" size="small">
              {{ getRegistrationTypeText(currentRecord.registration_type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.jobTitle')">
            {{ currentRecord.job_title || '-' }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.country')">
            {{ currentRecord.country }}
          </el-descriptions-item>
          <el-descriptions-item :label="$t('admin.registrations.table.registrationTime')" :span="2">
            {{ formatDate(currentRecord.created_at) }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
      <template #footer>
        <el-button @click="detailVisible = false">
          {{ $t('admin.registrations.modal.close') }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, Download } from '@element-plus/icons-vue'
import { getRegistrations, exportRegistrations } from '@/api/registration'
import { useI18n } from 'vue-i18n'

export default {
  name: 'AdminRegistrations',
  components: {
    Search,
    Refresh,
    Download
  },
  setup() {
    const { t } = useI18n()

    // 响应式数据
    const loading = ref(false)
    const tableData = ref([])
    const detailVisible = ref(false)
    const currentRecord = ref(null)

    // 搜索表单
    const searchForm = reactive({
      keyword: '',
      participationType: '',
      registrationType: '',
      dateRange: null
    })

    // 分页数据
    const pagination = reactive({
      page: 1,
      pageSize: 20,
      total: 0
    })

    // 获取数据
    const fetchData = async () => {
      loading.value = true
      try {
        const params = {
          page: pagination.page,
          per_page: pagination.pageSize,
          keyword: searchForm.keyword || undefined,
          participation_type: searchForm.participationType || undefined,
          registration_type: searchForm.registrationType || undefined
        }

        // 处理日期范围
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.start_date = searchForm.dateRange[0]
          params.end_date = searchForm.dateRange[1]
        }

        const response = await getRegistrations(params)
        if (response.success) {
          tableData.value = response.data
          pagination.total = response.pagination.total
        } else {
          ElMessage.error(response.message || t('common.error'))
        }
      } catch (error) {
        console.error('获取报名列表失败:', error)
        ElMessage.error(t('common.error'))
      } finally {
        loading.value = false
      }
    }

    // 搜索
    const handleSearch = () => {
      pagination.page = 1
      fetchData()
    }

    // 重置
    const handleReset = () => {
      searchForm.keyword = ''
      searchForm.participationType = ''
      searchForm.registrationType = ''
      searchForm.dateRange = null
      pagination.page = 1
      fetchData()
    }

    // 刷新
    const handleRefresh = () => {
      fetchData()
    }

    // 导出
    const handleExport = async () => {
      try {
        const params = {
          keyword: searchForm.keyword || undefined,
          participation_type: searchForm.participationType || undefined,
          registration_type: searchForm.registrationType || undefined
        }

        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.start_date = searchForm.dateRange[0]
          params.end_date = searchForm.dateRange[1]
        }

        const blob = await exportRegistrations(params)
        // 某些封装可能返回 { data: Blob }，兼容处理
        const dataBlob = blob instanceof Blob ? blob : (blob?.data instanceof Blob ? blob.data : null)
        if (!dataBlob) {
          ElMessage.error(t('common.error'))
          return
        }

        const url = window.URL.createObjectURL(dataBlob)
        const link = document.createElement('a')
        link.href = url
        const now = new Date()
        const ts = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        link.download = `registrations-${ts}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        ElMessage.success(t('common.exportSuccess'))
      } catch (error) {
        console.error('导出失败:', error)
        ElMessage.error(t('common.error'))
      }
    }

    // 查看详情
    const handleView = (row) => {
      currentRecord.value = row
      detailVisible.value = true
    }

    // 删除
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          t('common.deleteConfirm'),
          t('common.warning'),
          {
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel'),
            type: 'warning'
          }
        )

        // TODO: 实现删除API
        ElMessage.success(t('common.deleteSuccess'))
        fetchData()
      } catch {
        // 用户取消删除
      }
    }

    // 分页大小改变
    const handleSizeChange = (size) => {
      pagination.pageSize = size
      pagination.page = 1
      fetchData()
    }

    // 当前页改变
    const handleCurrentChange = (page) => {
      pagination.page = page
      fetchData()
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    // 获取参会方式文本
    const getParticipationTypeText = (type) => {
      const map = {
        offline: t('register.form.participationType.offline'),
        online: t('register.form.participationType.online')
      }
      return map[type] || type
    }

    // 获取注册类型文本
    const getRegistrationTypeText = (type) => {
      const map = {
        postgraduate: t('register.form.registrationType.postgraduate'),
        undergraduate: t('register.form.registrationType.undergraduate'),
        faculty: t('register.form.registrationType.faculty'),
        regular_attendee: t('register.form.registrationType.regular_attendee')
      }
      return map[type] || type
    }

    // 获取注册类型颜色
    const getRegistrationTypeColor = (type) => {
      const map = {
        postgraduate: 'primary',
        undergraduate: 'success',
        faculty: 'warning',
        regular_attendee: 'info'
      }
      return map[type] || 'info'
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData()
    })

    return {
      loading,
      tableData,
      searchForm,
      pagination,
      detailVisible,
      currentRecord,
      fetchData,
      handleSearch,
      handleReset,
      handleRefresh,
      handleExport,
      handleView,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
      formatDate,
      getParticipationTypeText,
      getRegistrationTypeText,
      getRegistrationTypeColor
    }
  }
}
</script>

<style scoped>
.admin-registrations {
  padding: 0;
}

.search-section,
.action-section,
.table-section {
  margin-bottom: 20px;
}

.search-form {
  padding: 10px 0;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.detail-content {
  padding: 20px 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .search-form .el-col {
    margin-bottom: 10px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .el-table {
    font-size: 12px;
  }
}
</style>